import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ToastService } from '../../../core/services/toast.service';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import { authActions } from 'angular/app/core/store/auth';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ClipboardService } from 'ngx-clipboard';
import { environment } from '../../../../environments/environment';
import { VendorsService } from '../../../core/services/vendors.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { ApiError } from '../../../core/models/common/api-error';
import { FilePondOptions } from 'filepond';
import { FileUploadComponent } from '../../../shared-manager/components/file-upload/file-upload.component';
import { SharedModule } from '../../../shared/shared.module';
import { SharedManagerModule } from '../../../shared-manager/shared-manager.module';
import { AdminTabsComponent } from '../../components/admin-tabs.component';
import { PaymentProvider } from '../../../core/models/entities/vendor';
import { phoneValidator } from '../../../core/services/validation.service';

@Component({
  selector: 'app-vendor-settings-page',
  template: `
    <app-page-header title="Organisation"></app-page-header>

    <app-spacer></app-spacer>

    @if (vendor()?.payment_provider === PaymentProvider.Payrexx) {
      <app-admin-tabs></app-admin-tabs>
      <app-spacer size="6"></app-spacer>
    }

    <form>
      <app-fieldset-card
        [showSaveButton]="true"
        [saving]="savingContact"
        (onSave)="saveContact()"
      >
        <div title>Coordonnées</div>
        <div description
          >Toutes les informations liées aux coordonnées de votre organisation,
          afin que les clients puissent vous contacter.
        </div>

        <app-text-input
          label="Nom"
          name="name"
          [control]="form.get('name')"
        ></app-text-input>

        <app-text-input
          label="Rue et no"
          name="address"
          [control]="form.get('address')"
        ></app-text-input>

        <div class="grid grid-cols-1 md:grid-cols-4 md:gap-x-6">
          <app-text-input
            label="Code postal"
            name="zip"
            [control]="form.get('zip')"
          ></app-text-input>

          <app-text-input
            label="Localité"
            name="city"
            [control]="form.get('city')"
            class="col-span-3"
          ></app-text-input>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 md:gap-x-6">
          <app-text-input
            label="Adresse e-mail"
            name="email"
            [control]="form.get('email')"
          ></app-text-input>

          <app-phone-input
            #phoneInput
            label="No de téléphone"
            name="phone"
            [control]="form.get('phone')"
          ></app-phone-input>
        </div>
      </app-fieldset-card>

      <app-spacer size="6"></app-spacer>

      <app-fieldset-card
        [showSaveButton]="true"
        [saving]="savingBilling"
        (onSave)="saveBilling()"
      >
        <div title>Informations de facturation</div>
        <div description
          >Les informations de facturation sont utilisées pour générer les
          factures.
        </div>

        <app-text-input
          [control]="form.controls['iban']"
          name="iban"
          label="IBAN"
        />

        <app-text-input
          label="N° de TVA"
          name="vat"
          [control]="form.get('vat')"
        ></app-text-input>
      </app-fieldset-card>

      <app-spacer size="6"></app-spacer>

      <app-fieldset-card
        [showSaveButton]="true"
        [saving]="savingLogo"
        (onSave)="saveLogo()"
      >
        <div title>Logo</div>
        <div description
          >Votre logo s'affichera sur votre page publique, les e-mails de
          confirmation et les factures.
        </div>

        <app-file-upload
          #logoUploadCmp
          [config]="logoUploadConfig"
          [defaultFiles]="
            authUser()?.vendor?.logo_url ? [authUser()?.vendor?.logo_url] : []
          "
        ></app-file-upload>
      </app-fieldset-card>

      <app-spacer size="6"></app-spacer>

      <app-fieldset-card
        [showSaveButton]="true"
        [saving]="savingTerms"
        (onSave)="saveTerms()"
      >
        <div title>Conditions générales</div>
        <div description
          >Vous pouvez importer ici un document au format PDF de vos conditions
          générales. Le client sera amené à accepter ces conditions générales
          pour pouvoir valider sa demande de réservation. Ces conditions
          générales sont valables pour toutes les salles, sauf pour celles où
          des conditions spéciales ont été ajoutées dans le menu de gestion de
          la salle.
        </div>

        <app-file-upload
          #generalTermsUploadCmp
          [config]="generalTermsUploadConfig"
          [defaultFiles]="
            authUser()?.vendor?.general_terms_public_url
              ? [authUser()?.vendor?.general_terms_public_url]
              : []
          "
        ></app-file-upload>
      </app-fieldset-card>

      <app-spacer size="6"></app-spacer>

      <app-fieldset-card
        [showSaveButton]="true"
        [saving]="savingUrl"
        (onSave)="saveUrl()"
      >
        <div title>Lien de partage</div>
        <div description
          >Partagez ce lien pour que vos clients puissent réserver en ligne.
        </div>

        <app-text-input
          [control]="form.get('slug')"
          name="slug"
          [prepend]="host + '/louer-salle/'"
          [appendAction]="{
            icon: 'copy',
            title: 'Copier le lien',
            action: copyLink.bind(this),
          }"
        ></app-text-input>
      </app-fieldset-card>
    </form>
  `,
  imports: [SharedModule, SharedManagerModule, AdminTabsComponent],
})
export class VendorSettingsPageComponent implements OnInit {
  @ViewChild('logoUploadCmp') logoUploadCmp: FileUploadComponent;
  @ViewChild('generalTermsUploadCmp')
  generalTermsUploadCmp: FileUploadComponent;
  @ViewChild('phoneInput') phoneInput: ElementRef;
  authUser = this.store.selectSignal(fromStore.selectAuthUser);
  vendor = this.store.selectSignal(fromStore.selectAuthVendor);
  form: FormGroup;

  // Individual loading states for partial saves
  savingContact = false;
  savingBilling = false;
  savingLogo = false;
  savingTerms = false;
  savingUrl = false;
  readonly logoUploadConfig: Partial<FilePondOptions> = {
    className: 'single-col',
    allowMultiple: false,
    maxFileSize: '1MB',
    acceptedFileTypes: [
      'image/png',
      'image/jpeg',
      'image/svg+xml',
      'image/webp',
    ],
    labelIdle: `
        <div>
          <i class="fal fa-file-image"></i>
          <div class="font-sans text-secondary-500">Faites glisser votre logo ici <br>ou <span class="underline cursor-pointer">téléchargez-le depuis votre appareil</span>.</div>
        </div>
      `,
  };
  readonly generalTermsUploadConfig: Partial<FilePondOptions> = {
    className: 'single-col',
    allowMultiple: false,
    acceptedFileTypes: ['application/pdf'],
    onactivatefile: () => {
      if (
        this.authUser().vendor.general_terms_public_url &&
        typeof this.generalTermsUploadCmp.files[0] === 'string'
      ) {
        window.open(this.authUser().vendor.general_terms_public_url, '_blank');
      }
    },
    labelIdle: `
        <div>
          <i class="fal fa-file-pdf"></i>
          <div class="font-sans text-secondary-500">Faites glisser les conditions générales ici <br>ou <span class="underline cursor-pointer">téléchargez-les depuis votre appareil</span>.</div>
        </div>
      `,
  };
  protected readonly PaymentProvider = PaymentProvider;

  constructor(
    private toastService: ToastService,
    private store: Store,
    private clipboardService: ClipboardService,
    private vendorsService: VendorsService,
    private formValidationService: FormValidationService,
  ) {}

  ngOnInit() {
    const user = this.authUser();
    this.form = new FormGroup({
      name: new FormControl(user.vendor.name || '', [Validators.required]),
      address: new FormControl(user.vendor.address || ''),
      zip: new FormControl(user.vendor.zip || ''),
      city: new FormControl(user.vendor.city || ''),
      vat: new FormControl(user.vendor.vat || ''),
      phone: new FormControl(user.vendor.phone || ''),
      email: new FormControl(user.vendor.email || ''),
      slug: new FormControl(user.vendor.slug || '', [Validators.required]),
      iban: new FormControl(user.vendor.iban || ''),
    });
  }

  ngAfterViewInit() {
    this.form.get('phone').setValidators([phoneValidator(this.phoneInput)]);
  }

  get host() {
    return new URL(environment.appUrl).host;
  }

  async saveContact() {
    if (!this.formValidationService.validate(this.form)) return;

    this.savingContact = true;
    try {
      const vendor = await this.vendorsService.update({
        name: this.form.get('name')?.value,
        slug: this.form.get('slug')?.value,
        address: this.form.get('address')?.value,
        zip: this.form.get('zip')?.value,
        city: this.form.get('city')?.value,
        email: this.form.get('email')?.value,
        phone: this.form.get('phone')?.value,
      });
      this.updateUserStore(vendor);
      this.toastService.success('Coordonnées enregistrées.');
    } catch (exception) {
      this.handleSaveError(exception);
    }
    this.savingContact = false;
  }

  async saveBilling() {
    if (!this.formValidationService.validate(this.form)) return;

    this.savingBilling = true;
    try {
      const vendor = await this.vendorsService.update({
        name: this.form.get('name')?.value,
        slug: this.form.get('slug')?.value,
        iban: this.form.get('iban')?.value,
        vat: this.form.get('vat')?.value,
      });
      this.updateUserStore(vendor);
      this.toastService.success('Informations de facturation enregistrées.');
    } catch (exception) {
      this.handleSaveError(exception);
    }
    this.savingBilling = false;
  }

  async saveLogo() {
    this.savingLogo = true;
    try {
      const vendor = await this.vendorsService.update({
        name: this.form.get('name')?.value,
        slug: this.form.get('slug')?.value,
        logo:
          typeof this.logoUploadCmp.files[0] === 'string'
            ? null
            : this.logoUploadCmp.files[0],
        delete_logo: !!(
          this.logoUploadCmp.files.length === 0 &&
          this.authUser().vendor.logo_url
        ),
      });
      this.updateUserStore(vendor);
      this.toastService.success('Logo enregistré.');
    } catch (exception) {
      this.handleSaveError(exception);
    }
    this.savingLogo = false;
  }

  async saveTerms() {
    this.savingTerms = true;
    try {
      const vendor = await this.vendorsService.update({
        name: this.form.get('name')?.value,
        slug: this.form.get('slug')?.value,
        general_terms:
          typeof this.generalTermsUploadCmp.files[0] === 'string'
            ? null
            : this.generalTermsUploadCmp.files[0],
        delete_general_terms: !!(
          this.generalTermsUploadCmp.files.length === 0 &&
          this.authUser().vendor.general_terms_public_url
        ),
      });
      this.updateUserStore(vendor);
      this.toastService.success('Conditions générales enregistrées.');
    } catch (exception) {
      this.handleSaveError(exception);
    }
    this.savingTerms = false;
  }

  async saveUrl() {
    if (!this.formValidationService.validate(this.form)) return;

    this.savingUrl = true;
    try {
      const vendor = await this.vendorsService.update({
        name: this.form.get('name')?.value,
        slug: this.form.get('slug')?.value,
      });
      this.updateUserStore(vendor);
      this.toastService.success('Lien de partage enregistré.');
    } catch (exception) {
      this.handleSaveError(exception);
    }
    this.savingUrl = false;
  }

  private updateUserStore(vendor: any) {
    const user = this.authUser();
    console.log('UPDATING STORE WITH VENDOR:', JSON.stringify(vendor, null, 2));

    this.store.dispatch(
      authActions.findCurrentUserSuccess({
        user: { ...user, vendor: { ...user.vendor, ...vendor } },
      }),
    );
  }

  private handleSaveError(exception: any) {
    this.formValidationService.displayApiValidationErrors(
      this.form,
      exception as ApiError,
    );
  }

  copyLink() {
    const user = this.authUser();

    this.clipboardService.copy(
      `${environment.appUrl}/louer-salle/${user.vendor.slug}`,
    );
    this.toastService.success('Lien copié dans le presse-papiers.');
  }
}
